import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Compass, ShieldCheck, MapPin } from "lucide-react";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { Button } from "@/components/ui/button";
import LocationMap from "@/components/LocationMap";
import { locations, primaryLocation } from "@/lib/locations-data";
import { business } from "@/lib/business-data";

const ServiceAreas = () => {
  const { openQuoteForm } = useQuoteForm();
  const others = locations.filter((l) => !l.isPrimary);

  return (
    <section id="locations" className="relative section-soft overflow-hidden">
      <div className="relative w-full">
        <LocationMap />

        {/* Floating glass panel over the map */}
        <div className="container-max relative z-10 -mt-24 md:-mt-32 lg:-mt-40 pb-16 md:pb-24 px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative mx-auto max-w-5xl rounded-[28px] border border-border/70 bg-card/95 backdrop-blur-xl p-8 md:p-10 lg:p-12"
            style={{
              boxShadow:
                "0 40px 80px -30px hsl(var(--accent-dark) / 0.4), 0 8px 24px -12px hsl(var(--primary) / 0.2)",
            }}
          >
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-start">
              <div>
                <span className="eyebrow-pill mb-5">
                  <Compass className="h-3 w-3" /> Coverage
                </span>
                <h2
                  className="font-heading text-foreground mb-4 tracking-tight font-bold leading-[1.05]"
                  style={{ fontSize: "clamp(2.25rem, 4.2vw, 3.75rem)" }}
                >
                  Areas We <span className="text-gradient-primary">Serve</span>
                </h2>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-7">
                  We work out of Hayward and cover {locations.length} cities within a{" "}
                  {business.serviceRadiusMiles}-mile radius — same rates, same response, no travel
                  surcharge anywhere in the area.
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-8">
                  <div className="flex items-center gap-2 rounded-xl bg-primary/10 border border-primary/25 px-3.5 py-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <span className="text-sm font-bold text-foreground">
                      {business.serviceRadiusMiles}-Mile Radius
                    </span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl bg-secondary/10 border border-secondary/25 px-3.5 py-2">
                    <MapPin className="h-4 w-4 text-secondary" />
                    <span className="text-sm font-bold text-foreground">
                      {locations.length} Cities Covered
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    size="lg"
                    onClick={openQuoteForm}
                    className="gap-2 px-6 rounded-xl shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.55)]"
                  >
                    Get Free Estimate <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button asChild variant="outline" size="lg" className="gap-2 px-6 rounded-xl">
                    <a href={business.phoneHref}>
                      <Phone className="h-4 w-4 text-primary" /> {business.phone}
                    </a>
                  </Button>
                </div>
              </div>

              {/* City list — text only, no images */}
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Cities We Serve
                  </span>
                  <Link
                    to="/locations"
                    className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1"
                  >
                    View all <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>

                <Link
                  to={`/locations/${primaryLocation.slug}`}
                  className="group mb-2 flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary/12 to-primary/5 border border-primary/30 px-4 py-3 hover:border-primary/60 hover:from-primary/18 transition-colors"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                  <p className="font-heading font-bold text-sm text-foreground truncate">
                    {primaryLocation.name}, {primaryLocation.state}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Home base · {business.address.street}</p>
                  </div>
                </Link>

                <div className="grid grid-cols-2 gap-2">
                  {others.map((loc) => (
                    <Link
                      key={loc.slug}
                      to={`/locations/${loc.slug}`}
                      className="group flex items-center gap-2 rounded-lg bg-background border border-border px-3 py-2.5 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    >
                      <span className="h-2 w-2 rounded-full bg-secondary shrink-0" />
                      <span className="text-sm font-semibold text-foreground truncate">{loc.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
